import React, { useState, useCallback, useRef, useEffect } from "react";
import { X, Check } from "react-feather"; // used when showSaveButton / showCancelButton is true
import { EditIcon } from "../../SVG/edit-icon";
import "./editable-text.scss";

export type EditableTextSize = "small" | "medium" | "large";

export type EditableTextVariant = "default" | "outlined" | "filled";

export interface EditableTextProps {
  /** Current text value (controlled mode) */
  value?: string;
  /** Initial text value (uncontrolled mode) */
  defaultValue?: string;
  /** Fired with the new value when the user confirms the edit */
  onSave?: (value: string) => void;
  /** Fired on every keystroke while editing */
  onChange?: (value: string) => void;
  /** Fired when the user cancels editing */
  onCancel?: () => void;
  /** Fired when the user enters edit mode */
  onEditStart?: () => void;
  /** Placeholder shown when the input is empty */
  placeholder?: string;
  /** Text shown when value is empty in view mode */
  emptyText?: string;
  /** Additional CSS class for the root element */
  className?: string;
  /** HTML id for the root element */
  id?: string;
  /** Prevents entering edit mode */
  disabled?: boolean;
  /** Hides the edit icon entirely */
  readOnly?: boolean;
  /** Size variant controlling height and font size */
  size?: EditableTextSize;
  /** Visual variant */
  variant?: EditableTextVariant;
  /** Maximum character length for the input */
  maxLength?: number;
  /** Minimum character length for the input */
  minLength?: number;
  /** Color of the edit pencil icon */
  iconColor?: string;
  /** Pixel size of the edit pencil icon */
  iconSize?: number;
  /** Override text color in view mode */
  textColor?: string;
  /** Override font weight in view mode */
  fontWeight?: number | string;
  /** Show the save (check) button in edit mode. Defaults to false. */
  showSaveButton?: boolean;
  /** Show the cancel (x) button in edit mode. Defaults to false. */
  showCancelButton?: boolean;
  /** Persist edit on blur instead of cancelling */
  saveOnBlur?: boolean;
  /** Persist edit when pressing Enter */
  saveOnEnter?: boolean;
  /** Cancel edit when pressing Escape */
  cancelOnEscape?: boolean;
  /** Custom React node to replace the default edit icon */
  editIcon?: React.ReactNode;
  /** Custom React node to replace the default save icon */
  saveIcon?: React.ReactNode;
  /** Custom React node to replace the default cancel icon */
  cancelIcon?: React.ReactNode;
  /** Fixed width for the input field (e.g. "200px") */
  inputWidth?: string;
  /** Border radius override */
  borderRadius?: string;
  /** Background color override */
  backgroundColor?: string;
  /** Padding override (e.g. "0.5rem 1rem") */
  padding?: string;
  /** Gap between the text and the edit icon */
  gap?: string;
  /** Accessible label for the component */
  ariaLabel?: string;
  /** Auto-select all text when entering edit mode */
  autoSelectOnEdit?: boolean;
  /** Validation function; return an error string or undefined */
  validate?: (value: string) => string | undefined;
  /** Tooltip text shown on hover over the edit icon */
  editTooltip?: string;
  /** Whether the component is in a loading / saving state */
  loading?: boolean;
  /** Input type (text, email, url, etc.) */
  inputType?: string;
}

export const EditableText: React.FC<EditableTextProps> = ({
  value: controlledValue,
  defaultValue = "",
  onSave,
  onChange,
  onCancel,
  onEditStart,
  placeholder = "Enter text...",
  emptyText = "—",
  className = "",
  id,
  disabled = false,
  readOnly = false,
  size = "medium",
  variant = "default",
  maxLength,
  minLength,
  iconColor = "#0056D6",
  iconSize,
  textColor,
  fontWeight,
  showSaveButton = false,
  showCancelButton = false,
  saveOnBlur = true,
  saveOnEnter = true,
  cancelOnEscape = true,
  editIcon,
  saveIcon,
  cancelIcon,
  inputWidth,
  borderRadius,
  backgroundColor,
  padding,
  gap,
  ariaLabel = "Editable text",
  autoSelectOnEdit = false,
  validate,
  editTooltip = "Edit",
  loading = false,
  inputType = "text",
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const resolvedValue = isControlled ? controlledValue : internalValue;

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(resolvedValue);
  const [validationError, setValidationError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(resolvedValue);
    }
  }, [resolvedValue, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (autoSelectOnEdit) {
        inputRef.current.select();
      }
    }
  }, [isEditing, autoSelectOnEdit]);

  const resolvedIconSize = iconSize ?? (size === "small" ? 12 : size === "large" ? 18 : 14);

  const handleEdit = useCallback(() => {
    if (disabled || loading) return;
    setEditValue(resolvedValue);
    setValidationError(undefined);
    setIsEditing(true);
    onEditStart?.();
  }, [resolvedValue, disabled, loading, onEditStart]);

  const handleSave = useCallback(() => {
    if (validate) {
      const error = validate(editValue);
      if (error) {
        setValidationError(error);
        return;
      }
    }
    if (minLength && editValue.length < minLength) {
      setValidationError(`Minimum ${minLength} characters required`);
      return;
    }
    setValidationError(undefined);
    if (!isControlled) {
      setInternalValue(editValue);
    }
    onSave?.(editValue);
    setIsEditing(false);
  }, [editValue, onSave, validate, minLength, isControlled]);

  const handleCancel = useCallback(() => {
    setEditValue(resolvedValue);
    setValidationError(undefined);
    setIsEditing(false);
    onCancel?.();
  }, [resolvedValue, onCancel]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVal = e.target.value;
      setEditValue(newVal);
      onChange?.(newVal);
      if (validationError && validate) {
        const error = validate(newVal);
        if (!error) setValidationError(undefined);
      }
    },
    [onChange, validate, validationError]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (saveOnEnter && e.key === "Enter") {
        e.preventDefault();
        handleSave();
      }
      if (cancelOnEscape && e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [saveOnEnter, cancelOnEscape, handleSave, handleCancel]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!rootRef.current?.contains(e.relatedTarget as Node)) {
        if (saveOnBlur) {
          handleSave();
        } else {
          handleCancel();
        }
      }
    },
    [saveOnBlur, handleSave, handleCancel]
  );

  const displayValue = resolvedValue || emptyText;

  const rootStyle: React.CSSProperties = {};
  if (borderRadius) rootStyle.borderRadius = borderRadius;
  if (backgroundColor) rootStyle.backgroundColor = backgroundColor;
  if (padding) rootStyle.padding = padding;
  if (gap) rootStyle.gap = gap;

  const rootClasses = [
    "editable-text",
    `editable-text--${size}`,
    `editable-text--${variant}`,
    isEditing ? "editable-text--editing" : "",
    disabled ? "editable-text--disabled" : "",
    loading ? "editable-text--loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (isEditing) {
    return (
      <div
        ref={rootRef}
        className={rootClasses}
        id={id}
        style={rootStyle}
        role="group"
        aria-label={ariaLabel}
      >
        <div className="editable-text__input-wrapper">
          <input
            ref={inputRef}
            type={inputType}
            value={editValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={placeholder}
            maxLength={maxLength}
            className={`editable-text__input ${validationError ? "editable-text__input--error" : ""}`}
            style={{ width: inputWidth }}
            aria-label={ariaLabel}
            aria-invalid={!!validationError}
          />
          {validationError && (
            <span className="editable-text__error">{validationError}</span>
          )}
        </div>
        {showSaveButton && (
          <button
            type="button"
            onClick={handleSave}
            className="editable-text__action-btn editable-text__save-btn"
            aria-label="Save"
            tabIndex={0}
          >
            {saveIcon ?? <Check size={resolvedIconSize + 2} strokeWidth={2.5} />}
          </button>
        )}
        {showCancelButton && (
          <button
            type="button"
            onClick={handleCancel}
            className="editable-text__action-btn editable-text__cancel-btn"
            aria-label="Cancel"
            tabIndex={0}
          >
            {cancelIcon ?? <X size={resolvedIconSize + 2} strokeWidth={2.5} />}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={rootClasses}
      id={id}
      style={rootStyle}
      role="group"
      aria-label={ariaLabel}
    >
      <span
        className={`editable-text__display ${!resolvedValue ? "editable-text__display--empty" : ""}`}
        style={{ color: textColor, fontWeight }}
      >
        {displayValue}
      </span>
      {!readOnly && (
        <button
          type="button"
          onClick={handleEdit}
          className="editable-text__edit-btn"
          aria-label={editTooltip}
          title={editTooltip}
          disabled={disabled || loading}
          tabIndex={0}
        >
          {editIcon ?? <EditIcon color={iconColor} size={resolvedIconSize} />}
        </button>
      )}
    </div>
  );
};
